import { useEffect } from 'react';

import type { SeoConfig } from '../lib/seo';

type SeoProps = {
  config: SeoConfig;
};

export function Seo({ config }: SeoProps) {
  useEffect(() => {
    applySeo(config);
  }, [config]);

  return null;
}

function applySeo(config: SeoConfig) {
  document.documentElement.lang = config.ogLocale === 'de_CH' ? 'de-CH' : 'en';
  document.title = config.title;
  upsertMeta('name', 'description', config.description);
  upsertLink('canonical', config.canonical);

  document.querySelectorAll('link[data-managed-seo="alternate"]').forEach((element) => {
    element.remove();
  });

  for (const alternate of config.alternates) {
    const link = document.createElement('link');
    link.rel = 'alternate';
    link.hreflang = alternate.hrefLang;
    link.href = alternate.href;
    link.dataset.managedSeo = 'alternate';
    document.head.append(link);
  }

  if (config.noindex) {
    upsertMeta('name', 'robots', 'noindex, follow');
  } else {
    document.querySelector('meta[name="robots"]')?.remove();
  }

  upsertMeta('property', 'og:title', config.ogTitle);
  upsertMeta('property', 'og:description', config.ogDescription);
  upsertMeta('property', 'og:url', config.canonical);
  upsertMeta('property', 'og:type', config.ogType);
  upsertMeta('property', 'og:locale', config.ogLocale);
  upsertMeta('property', 'og:image', config.image);
  upsertMeta('property', 'og:image:width', '1200');
  upsertMeta('property', 'og:image:height', '630');
  upsertMeta('name', 'twitter:card', 'summary_large_image');
  upsertMeta('name', 'twitter:title', config.ogTitle);
  upsertMeta('name', 'twitter:description', config.ogDescription);
  upsertMeta('name', 'twitter:image', config.image);

  document.querySelector('script[data-managed-seo="json-ld"]')?.remove();
  if (config.jsonLd) {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.textContent = JSON.stringify(config.jsonLd);
    script.dataset.managedSeo = 'json-ld';
    document.head.append(script);
  }
}

function upsertMeta(attribute: 'name' | 'property', key: string, content: string) {
  let meta = document.querySelector<HTMLMetaElement>(`meta[${attribute}="${key}"]`);

  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute(attribute, key);
    document.head.append(meta);
  }

  meta.content = content;
}

function upsertLink(rel: string, href: string) {
  let link = document.querySelector<HTMLLinkElement>(`link[rel="${rel}"]`);

  if (!link) {
    link = document.createElement('link');
    link.rel = rel;
    document.head.append(link);
  }

  link.href = href;
}
